import React, { useEffect, useState } from 'react';
import ApprovalModal from './ApprovalModal/ApprovalModal';
import RejectionModal from './RejectionModal/RejectionModal';
import RevisionHistoryModal from './RevisionHistoryModal/RevisionHistoryModal';

import { ArrowLeft, CheckCircle, ChevronDown, Clock, Eye } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { topicPersonSummary } from '../../../redux/actions/topics';
import { apiUpdateEvaluationStatus } from '../../../redux/api/evaluationTaskApi';
import { apiListExpectedScores, apiListPendingEvaluations } from '../../../redux/api/expectedScoreApi';
import './Collaborators.scss';
import { bloomsDefaults, getTaxonomyLevel } from './taxonomyConstants';

const findExpectedScoreFor = (expectedScores, topicId, department, role) => {
  if (!Array.isArray(expectedScores) || expectedScores.length === 0) return null;
  if (!topicId || !department || !role) return null;
  const dept = String(department).toLowerCase();
  const roleKey = String(role).toLowerCase();
  return expectedScores.find(es =>
    es.topic_id === topicId &&
    typeof es.department === 'string' && es.department.toLowerCase() === dept &&
    typeof es.role === 'string' && es.role.toLowerCase() === roleKey
  ) || null;
};

const Collaborators = ({ employee, exit }) => {
  const [detailedAssessment, setDetailedAssessment] = useState(null);
  const dispatch = useDispatch();
  const topicsPersonData = useSelector(state => state.topics.person.data);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompetencies, setIsLoadingCompetencies] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [approvalModal, setApprovalModal] = useState({ isOpen: false, assessment: null, employeeName: '' });
  const [rejectionModal, setRejectionModal] = useState({ isOpen: false, assessment: null, employeeName: '' });
  const [revisionHistoryModal, setRevisionHistoryModal] = useState({ isOpen: false, competency: null });
  const [expectedScores, setExpectedScores] = useState([]);

  React.useEffect(() => {
    setSelectedEmployee(employee);
    apiListExpectedScores().then((json) => {
      setExpectedScores(json.expected_scores);
    })
  }, [employee]);

  const handleApproveClick = (assessment, employeeName) => {
    setApprovalModal({
      isOpen: true,
      assessment,
      employeeName
    });
  };

  const handleRejectClick = (assessment, employeeName) => {
    setRejectionModal({
      isOpen: true,
      assessment,
      employeeName
    });
  };

  const handleConfirmApproval = (feedback) => {
    if (approvalModal.assessment) {
      const assessment = approvalModal.assessment;

      setIsApproving(true);
      apiUpdateEvaluationStatus(employee.id, assessment.id, 'APPROVED', feedback)
        .then(response => {
          setSelectedEmployee(prevEmployee => ({
            ...prevEmployee,
            pendingAssessments: prevEmployee.pendingAssessments.map(a =>
              a.id === assessment.id
                ? { ...a, status: 'approved' }
                : a
            )
          }));

          dispatch(topicPersonSummary(employee.id, 1));

          setApprovalModal({ isOpen: false, assessment: null, employeeName: '' });
        })
        .catch(error => {
          setApprovalModal({ isOpen: false, assessment: null, employeeName: '' });
        })
        .finally(() => {
          setIsApproving(false);
        });
    }
  };

  const handleConfirmRejection = (reason, requestedScore) => {
    if (rejectionModal.assessment) {
      const assessment = rejectionModal.assessment;

      setIsRejecting(true);
      apiUpdateEvaluationStatus(employee.id, assessment.id, 'REVISION_REQUESTED', reason, requestedScore)
        .then(response => {
          setSelectedEmployee(prevEmployee => ({
            ...prevEmployee,
            pendingAssessments: prevEmployee.pendingAssessments.map(a =>
              a.id === assessment.id
                ? { ...a, status: 'revision_requested' }
                : a
            )
          }));

          dispatch(topicPersonSummary(employee.id, 1));

          setRejectionModal({ isOpen: false, assessment: null, employeeName: '' });
        })
        .catch(error => {
          setRejectionModal({ isOpen: false, assessment: null, employeeName: '' });
        })
        .finally(() => {
          setIsRejecting(false);
        });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (selectedEmployee?.competenciesPagination?.totalPages || 1)) {
      setIsLoadingCompetencies(true);

      dispatch(topicPersonSummary(employee.id, newPage))
        .then(() => {
          setIsLoadingCompetencies(false);
        })
        .catch(error => {
          setIsLoadingCompetencies(false);
        });
    }
  };

  useEffect(() => {
    if (employee?.id) {
      if (employee.pendingAssessmentsCount === 0) {
        setSelectedEmployee(prevEmployee => ({
          ...prevEmployee,
          pendingAssessments: []
        }));
        return;
      }

      if (employee.pendingAssessments !== undefined) {
        const mappedAssessments = employee.pendingAssessments.map(assessment => {
          const matchingExpectedScore = findExpectedScoreFor(expectedScores, assessment.topic?.id, employee?.department, employee?.role);

          return {
            ...assessment,
            topic: assessment.topic,
            expectedScore: matchingExpectedScore?.expected_score || null,
            expectedScoresLoaded: expectedScores && Array.isArray(expectedScores)
          };
        });

        setSelectedEmployee(prevEmployee => ({
          ...prevEmployee,
          pendingAssessments: mappedAssessments
        }));
        return;
      }

      setIsLoading(true);
      apiListPendingEvaluations(employee.id).then(response => {
        const mappedAssessments = response.map(evaluation => {
          const matchingExpectedScore = findExpectedScoreFor(expectedScores, evaluation.topic?.id, employee?.department, employee?.role);

          return {
            id: evaluation.id,
            name: evaluation.topic.name,
            selfScore: evaluation.value,
            companyAverage: 0,
            level: evaluation.topic.symbol || 'N/A',
            submittedDate: evaluation.evaluatedAt,
            status: 'pending',
            topic: evaluation.topic,
            expectedScore: matchingExpectedScore?.expected_score || null,
            expectedScoresLoaded: expectedScores && Array.isArray(expectedScores)
          };
        });

        setSelectedEmployee(prevEmployee => ({
          ...prevEmployee,
          pendingAssessments: mappedAssessments
        }));
      }).catch(error => {
        setSelectedEmployee(prevEmployee => ({
          ...prevEmployee,
          pendingAssessments: []
        }));
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [employee?.id, employee?.pendingAssessments, employee?.pendingAssessmentsCount, employee?.department, employee?.role, expectedScores]);

  useEffect(() => {
    if (employee?.id) {
      setIsLoadingCompetencies(true);
      dispatch(topicPersonSummary(employee.id, 1))
        .then(() => {
          setIsLoadingCompetencies(false);
        })
        .catch(error => {
          setSelectedEmployee(prevEmployee => ({
            ...prevEmployee,
            competencies: []
          }));
          setIsLoadingCompetencies(false);
        });
    }
  }, [employee?.id, dispatch]);

  useEffect(() => {
    if (topicsPersonData?.items) {
      const mappedCompetencies = topicsPersonData.items.map(competency => {
        const expectedScoresLoaded = expectedScores && Array.isArray(expectedScores);

        const matchingExpectedScore = findExpectedScoreFor(expectedScores, competency.topic.id, employee?.department, employee?.role);

        const score = competency.lastValue || 0;
        const fallbackExpected = competency.topic.expected_score;
        const expected = matchingExpectedScore?.expected_score ?? fallbackExpected;
        const finalExpected = expected > 0 ? expected : 10;

        return {
          id: competency.topic.id,
          name: competency.topic.name,
          level: getTaxonomyLevel(score),
          score: score,
          companyAverage: competency.topic.tracking?.average ?? competency.companyAverage?.average ?? null,
          description: competency.topic.description || 'Sem descrição disponível',
          expectedScore: finalExpected,
          expectedScoresLoaded: expectedScoresLoaded,
          requestedScore: competency.requestedScore,
          managerReviewStatus: competency.managerReviewStatus
        };
      });

      setSelectedEmployee(prevEmployee => ({
        ...prevEmployee,
        competencies: mappedCompetencies,
        competenciesPagination: {
          currentPage: topicsPersonData.page || 1,
          totalPages: topicsPersonData.totalPages || 1,
          totalItems: topicsPersonData.totalItems || 0,
          nextPage: topicsPersonData.nextPage,
          prevPage: topicsPersonData.prevPage
        }
      }));
    }
  }, [topicsPersonData, expectedScores, employee?.department, employee?.role]);

  const PendingAssessmentsSkeleton = () => (
    <div className="collaborators-pending-section">
      <div className="collaborators-table-header">
        <h3>
          <Clock className="icon-md orange" />
          <span>Avaliações</span>
        </h3>
        <p>Carregando avaliações...</p>
      </div>
      <div className="collaborators-table-scroll">
        <table className="collaborators-table skeleton-table">
          <thead>
            <tr>
              <th>Competência</th>
              <th>Autoavaliação / Nota Esperada</th>
              <th>Enviado</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr key={index} className="skeleton-row">
                <td>
                  <div className="skeleton skeleton-text long"></div>
                </td>
                <td>
                  <div className="skeleton skeleton-score"></div>
                </td>
                <td>
                  <div className="skeleton skeleton-date"></div>
                </td>
                <td>
                  <div className="skeleton skeleton-status"></div>
                </td>
                <td>
                  <div className="skeleton-actions">
                    <div className="skeleton skeleton-button"></div>
                    <div className="skeleton skeleton-button"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const CompetenciesSkeleton = () => (
    <div className="collaborators-table-wrapper">
      <div className="collaborators-table-header">
        <h3>Competências</h3>
        <p>Carregando competências...</p>
      </div>
      <div className="collaborators-table-scroll">
        <table className="collaborators-table skeleton-table">
          <thead>
            <tr>
              <th>Competência</th>
              <th>Autoavaliação / Nota Esperada</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(6)].map((_, index) => (
              <tr key={index} className="skeleton-row">
                <td>
                  <div className="skeleton skeleton-text long"></div>
                </td>
                <td>
                  <div className="skeleton skeleton-text short"></div>
                </td>
                <td>
                  <div className="skeleton skeleton-text medium"></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="collaborators-modal-overlay">
      <div className="collaborators-modal">
        <ApprovalModal
          isOpen={approvalModal.isOpen}
          onClose={() => setApprovalModal({ isOpen: false, assessment: null, employeeName: '' })}
          assessment={approvalModal.assessment}
          employeeName={approvalModal.employeeName}
          onConfirm={handleConfirmApproval}
          isLoading={isApproving}
        />
        <RejectionModal
          isOpen={rejectionModal.isOpen}
          onClose={() => setRejectionModal({ isOpen: false, assessment: null, employeeName: '' })}
          assessment={rejectionModal.assessment}
          employeeName={rejectionModal.employeeName}
          onConfirm={handleConfirmRejection}
          isLoading={isRejecting}
          investment={rejectionModal.assessment?.topic}
        />
        {revisionHistoryModal.competency && selectedEmployee && (
          <RevisionHistoryModal
            isOpen={revisionHistoryModal.isOpen}
            onClose={() => setRevisionHistoryModal({ isOpen: false, competency: null })}
            competencyName={revisionHistoryModal.competency.name}
            employeeName={selectedEmployee.name}
            selfScore={revisionHistoryModal.competency.score}
            requestedScore={revisionHistoryModal.competency.requestedScore}
            expectedScore={revisionHistoryModal.competency.expectedScore}
          />
        )}
        <div className="collaborators-container">
          <div className="collaborators-header">
            <button
              onClick={() => {
                exit(true);
              }}
              className="collaborators-back-btn"
            >
              <ArrowLeft className="icon-sm" />
              <span>Voltar aos Investidores</span>
            </button>
            <div className="collaborators-header-row">
              <div>
                <h1 className="collaborators-title">{employee.name}</h1>
                <div className="collaborators-meta">
                  <span>{employee?.department}</span>
                  <span>•</span>
                  <span>{employee?.role}</span>
                </div>
              </div>
            </div>
          </div>
          {isLoading ? (
            <PendingAssessmentsSkeleton />
          ) : selectedEmployee?.pendingAssessments?.length === 0 ? (
            <div className="collaborators-pending-section">
              <div className="collaborators-table-header">
                <h3>
                  <Clock className="icon-md orange" />
                  <span>Avaliações Pendentes de Aprovação</span>
                </h3>
                <p>Não existem pendências no momento.</p>
              </div>
            </div>
          ) : (
            selectedEmployee?.pendingAssessments?.length > 0 && (
              <div className="collaborators-pending-section">
                <div className="collaborators-table-header">
                  <h3>
                    <Clock className="icon-md orange" />
                    <span>Avaliações Pendentes de Aprovação ({selectedEmployee?.pendingAssessments?.filter(assessment => assessment.status === 'pending').length || 0})</span>
                  </h3>
                  <p>Autoavaliações enviadas pelo colaborador</p>
                </div>
                <div className="collaborators-table-scroll">
                  <table className="collaborators-table">
                    <thead>
                      <tr>
                        <th>Competência</th>
                        <th>Autoavaliação / Nota Esperada</th>
                        <th>Enviado</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEmployee?.pendingAssessments.map((assessment) => (
                        <React.Fragment key={assessment.id}>
                          <tr className="collaborators-pending-row" onClick={() => setDetailedAssessment(detailedAssessment === assessment.id ? null : assessment.id)} style={{ cursor: 'pointer' }}>
                            <td>
                              <div className="collaborators-competency-name-container">
                                <div className="collaborators-competency-name">{assessment.name}</div>
                                <ChevronDown
                                  className={`collaborators-accordion-arrow ${detailedAssessment === assessment.id ? 'rotated' : ''}`}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="collaborators-score">
                                <span>{assessment.selfScore}</span>
                                {assessment.expectedScore ? (
                                  <span>/{assessment.expectedScore}</span>
                                ) : assessment.expectedScoresLoaded ? (
                                  <span>/-</span>
                                ) : (
                                  <span>/10</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="collaborators-date">{new Date(assessment.submittedDate).toLocaleDateString('pt-BR')}</span>
                            </td>
                            <td>
                              {assessment.status === 'revision_requested' ? (
                                <span className="collaborators-status orange-bg">Nota Revisada</span>
                              ) : assessment.status === 'approved' ? (
                                <span className="collaborators-status green-bg">Aprovado</span>
                              ) : (
                                <span className="collaborators-status">Pendente</span>
                              )}
                            </td>
                            <td>
                              {assessment.status !== 'revision_requested' && assessment.status !== 'approved' && (
                                <div className="collaborators-actions">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleApproveClick(assessment, selectedEmployee.name); }}
                                    className="collaborators-approve-btn"
                                  >
                                    <CheckCircle className="icon-xs" />
                                    <span>Aprovar</span>
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleRejectClick(assessment, selectedEmployee.name); }}
                                    className="collaborators-reject-btn "
                                  >
                                    <Eye className="icon-xs " />
                                    <span>Revisar</span>
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {detailedAssessment === assessment.id && (
                            <tr className="competency-detail-row">
                              <td colSpan={5} className="competency-detail-cell">
                                <div className="competency-detail-content competency-detail-padding">
                                  <div className="competency-detail-bg competency-detail-rounded competency-detail-block-margin">
                                    <h4 className="competency-detail-title-margin competency-detail-title-color competency-detail-title-size competency-detail-title-weight">Descrição da Competência</h4>
                                    <p className="competency-detail-desc-color competency-detail-desc-size competency-detail-desc-line">
                                      {assessment.topic.description || 'Sem descrição disponível'}
                                    </p>
                                  </div>
                                  <div className="competency-detail-bg competency-detail-rounded competency-detail-block-margin">
                                    <h4 className="competency-detail-title-margin competency-detail-title-color competency-detail-title-size competency-detail-title-weight">Nível de Competência - Análise de Gap</h4>
                                    <div className="competency-detail-relative">
                                      <div className="competency-detail-flex competency-detail-justify-between competency-detail-labels-margin">
                                        {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map((value, index) => (
                                          <div key={index} className="competency-detail-flex competency-detail-flex-col competency-detail-items-center competency-detail-label-size">
                                            <span className="competency-detail-label-color">{value}</span>
                                            <div className={`competency-detail-label-tick ${value % 1 !== 0 ? 'competency-detail-label-dot' : ''}`}></div>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="competency-detail-relative competency-detail-bar-height competency-detail-bar-bg competency-detail-bar-rounded competency-detail-bar-margin">
                                        <div className="competency-detail-bar-absolute competency-detail-bar-inset competency-detail-bar-flex competency-detail-bar-rounded competency-detail-bar-overflow">
                                          <div className="competency-detail-bar-col competency-detail-bar-red"></div>
                                          <div className="competency-detail-bar-col competency-detail-bar-orange"></div>
                                          <div className="competency-detail-bar-col competency-detail-bar-yellow"></div>
                                          <div className="competency-detail-bar-col competency-detail-bar-purple"></div>
                                          <div className="competency-detail-bar-col competency-detail-bar-blue"></div>
                                          <div className="competency-detail-bar-col competency-detail-bar-green"></div>
                                        </div>
                                        <div
                                          className={`competency-detail-marker competency-detail-marker-self score-${Math.floor(assessment.selfScore)}`}
                                          style={{ left: `${(assessment.selfScore / 10) * 100}%` }}
                                        >
                                          <div className={`competency-detail-marker-label competency-detail-marker-label-self score-${Math.floor(assessment.selfScore)}`}>
                                            Autoavaliação: {assessment.selfScore}
                                          </div>
                                        </div>
                                        <div
                                          className="competency-detail-marker competency-detail-marker-expected"
                                          style={{ left: `${(assessment.expectedScore / 10) * 100}%` }}
                                        >
                                          <div className="competency-detail-marker-label competency-detail-marker-label-expected">
                                            Esperado: {assessment.expectedScore}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="competency-detail-gap-block">
                                        <div className="competency-detail-flex competency-detail-items-center competency-detail-justify-between competency-detail-gap-size">
                                          <span className="competency-detail-gap-label">Análise do Gap:</span>
                                          <div className="competency-detail-flex competency-detail-gap-space">
                                            {assessment.selfScore >= assessment.expectedScore ? (
                                              <span className="competency-detail-gap-above">
                                                +{(assessment.selfScore - assessment.expectedScore).toFixed(1)} pontos acima do esperado
                                              </span>
                                            ) : (
                                              <span className="competency-detail-gap-below">
                                                -{(assessment.expectedScore - assessment.selfScore).toFixed(1)} pontos abaixo do esperado
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="competency-detail-legend-grid">
                                        {(() => {
                                          const bloom = assessment.topic || {};
                                          return (
                                            <>
                                              <div className="competency-detail-legend-item competency-detail-legend-red">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-red"></div>
                                                <span className="competency-detail-legend-title">LEMBRO</span>
                                                <div className="competency-detail-legend-range">0-1</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_remember?.trim() || bloomsDefaults.bloom_remember}</p>
                                              </div>
                                              <div className="competency-detail-legend-item competency-detail-legend-orange">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-orange"></div>
                                                <span className="competency-detail-legend-title">COMPREENDO</span>
                                                <div className="competency-detail-legend-range">1.5-3.5</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_understand?.trim() || bloomsDefaults.bloom_understand}</p>
                                              </div>
                                              <div className="competency-detail-legend-item competency-detail-legend-yellow">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-yellow"></div>
                                                <span className="competency-detail-legend-title">APLICO</span>
                                                <div className="competency-detail-legend-range">4-5.5</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_apply?.trim() || bloomsDefaults.bloom_apply}</p>
                                              </div>
                                              <div className="competency-detail-legend-item competency-detail-legend-purple">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-purple"></div>
                                                <span className="competency-detail-legend-title">ANALISO</span>
                                                <div className="competency-detail-legend-range">6-7.5</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_analyse?.trim() || bloomsDefaults.bloom_analyse}</p>
                                              </div>
                                              <div className="competency-detail-legend-item competency-detail-legend-blue">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-blue"></div>
                                                <span className="competency-detail-legend-title">AVALIO</span>
                                                <div className="competency-detail-legend-range">8-9.5</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_evaluate?.trim() || bloomsDefaults.bloom_evaluate}</p>
                                              </div>
                                              <div className="competency-detail-legend-item competency-detail-legend-green">
                                                <div className="competency-detail-legend-bar competency-detail-legend-bar-green"></div>
                                                <span className="competency-detail-legend-title">CRIO</span>
                                                <div className="competency-detail-legend-range">10</div>
                                                <p className="competency-detail-legend-desc">{bloom.bloom_create?.trim() || bloomsDefaults.bloom_create}</p>
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
          {isLoadingCompetencies ? (
            <CompetenciesSkeleton />
          ) : (
            <div className="collaborators-table-wrapper">
              <div className="collaborators-table-header">
                <h3>Competências</h3>
                <p>{selectedEmployee?.competencies?.length ?? 0} competências cadastradas</p>
              </div>
              <div className="collaborators-table-scroll">
                <table className="collaborators-table">
                  <thead>
                    <tr>
                      <th>Competência</th>
                      <th>Nível</th>
                      <th>Pontuação Atual</th>
                      <th>Média da Empresa</th>
                      <th>Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEmployee?.competencies?.map((competency) => {
                      const score = competency.requestedScore ?? competency.score;
                      const avg = competency.companyAverage;
                      const getScoreVsAvg = (s, a) => {
                        if (s == null || a == null) return null;
                        if (s > a) return 'above';
                        if (s < a) return 'below';
                        return 'equal';
                      };
                      const scoreVsAvg = getScoreVsAvg(score, avg);
                      return (
                        <tr key={competency.id} className="collaborators-competency-row">
                          <td>
                            <div className="collaborators-competency-name">{competency.name}</div>
                          </td>
                          <td>
                            <span className="collaborators-level-badge">
                              {competency.level}
                            </span>
                          </td>
                          <td>
                            <div className={`collaborators-score collaborators-score-current${scoreVsAvg ? ` score-vs-avg--${scoreVsAvg}` : ''}`}>
                              <span className="score-value">{score}</span>
                              <span className="score-suffix">/{competency.expectedScore}</span>
                              {competency.requestedScore != null && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setRevisionHistoryModal({ isOpen: true, competency }); }}
                                  className="score-history-btn"
                                >
                                  <Eye className="icon-xs" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="collaborators-score collaborators-score-average">
                              <span className="score-value">{avg ?? '—'}</span>
                              {avg != null && <span className="score-suffix">/{competency.expectedScore}</span>}
                            </div>
                          </td>
                          <td>
                            <div className="collaborators-description" title={competency.description}>
                              {competency.description}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {selectedEmployee?.competenciesPagination?.totalPages > 1 && (
                <div className="collaborators-pagination">
                  <div className="pagination-info">
                    <div className="page-indicator">
                      <span>Página</span>
                      <span className="page-number">{selectedEmployee.competenciesPagination.currentPage}</span>
                      <span className="total-pages">de {selectedEmployee.competenciesPagination.totalPages}</span>
                    </div>
                    <div className="items-count">
                      <span className="items-number">{selectedEmployee.competenciesPagination.totalItems}</span>
                      <span>competências no total</span>
                    </div>
                  </div>
                  <div className="pagination-controls">
                    <button
                      onClick={() => handlePageChange(selectedEmployee.competenciesPagination.currentPage - 1)}
                      disabled={!selectedEmployee.competenciesPagination.prevPage || isLoadingCompetencies}
                      className="pagination-btn"
                    >
                      <ArrowLeft className="btn-icon" />
                      <span>Anterior</span>
                    </button>

                    <div className="page-separator"></div>

                    <div className="current-page-display">
                      {selectedEmployee.competenciesPagination.currentPage}
                    </div>

                    <div className="page-separator"></div>

                    <button
                      onClick={() => handlePageChange(selectedEmployee.competenciesPagination.currentPage + 1)}
                      disabled={!selectedEmployee.competenciesPagination.nextPage || isLoadingCompetencies}
                      className="pagination-btn"
                    >
                      <span>Próxima</span>
                      <ArrowLeft className="btn-icon" style={{ transform: 'rotate(180deg)' }} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Collaborators;